import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import FooterPopularSearches from './FooterPopularSearches';
import BrandLogo from '../BrandLogo';
import { isGiftFinderLinkVisible } from '../../giftFinder/giftFinderSettings';
import { isCustomJewelryLinkVisible } from '../../jewelry/customJewelrySettings';

function footerHoursLine(site) {
  const mon = String(site?.hours_mon_fri || '').trim();
  const sat = String(site?.hours_sat || '').trim();
  if (mon && sat) return `${mon} · Sat ${sat}`;
  return mon || sat || '';
}

function FooterSocialIconLinks({ site, brandName }) {
  const instagramUrl = String(site?.instagram_url || '').trim();
  const whatsappHref = String(site?.whatsapp_href || 'https://wa.me/919680384332').trim();
  if (!instagramUrl && !whatsappHref) return null;

  return (
    <div className="site-footer-social-icons" role="list">
      {instagramUrl ? (
        <a
          href={instagramUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="site-footer-social-icons__btn"
          title="Instagram"
          aria-label={`${brandName} on Instagram`}
          role="listitem"
        >
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/instagram.svg"
            alt=""
            className="site-footer-social-icons__img site-footer-social-icons__img--instagram"
            width={20}
            height={20}
          />
        </a>
      ) : null}
      {whatsappHref ? (
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          className="site-footer-social-icons__btn"
          title="WhatsApp"
          aria-label="Contact on WhatsApp"
          role="listitem"
        >
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/whatsapp.svg"
            alt=""
            className="site-footer-social-icons__img site-footer-social-icons__img--whatsapp"
            width={20}
            height={20}
          />
        </a>
      ) : null}
    </div>
  );
}

function FooterContactLinks({ site }) {
  const telHref = site?.tel_href || 'tel:+919680384332';
  const phone = site?.phone_display || '+91 9680384332';
  const emailHref = site?.email_href || 'mailto:salonidesigns.floral@gmail.com';
  const email = site?.email || 'salonidesigns.floral@gmail.com';

  return (
    <div className="site-footer-contact-links">
      <a href={telHref} className="site-footer-list-link">
        {phone}
      </a>
      <a href={emailHref} className="site-footer-list-link site-footer-list-link--wrap">
        {email}
      </a>
    </div>
  );
}

function FooterNavColumn({ heading, children, className = '' }) {
  return (
    <div className={`site-footer-col site-footer-col--nav ${className}`.trim()}>
      <h2 className="site-footer-heading">{heading}</h2>
      <ul className="site-footer-list">{children}</ul>
    </div>
  );
}

function FooterNavItem({ to, onClick, children, end = false }) {
  return (
    <li>
      <NavLink to={to} end={end} className="site-footer-list-link" onClick={onClick}>
        {children}
      </NavLink>
    </li>
  );
}

/**
 * Storefront doormat footer — Tonoto-style 2×2 link grid on mobile.
 */
export default function SiteFooter({
  site,
  brandName,
  brandLogo,
  footerCopy,
  store,
  onNavLinkClick,
  authReady,
  isAuthed,
  isAdmin,
  footerLoginTo,
  footerLoginSearch,
}) {
  const hoursLine = footerHoursLine(site);

  return (
    <footer className="site-footer" role="contentinfo">
      <div className="container site-footer-container">
        <div className="site-footer-layout">
          <div className="site-footer-col site-footer-col--brand">
            <Link
              to="/"
              className="site-footer-brand-block text-decoration-none"
              onClick={onNavLinkClick('/', { end: true })}
            >
              <BrandLogo
                src={brandLogo}
                alt=""
                className="footer-logo"
                size={44}
                aria-hidden
              />
              <div className="site-footer-brand-text">
                <span className="footer-brand">{brandName}</span>
                <span className="site-footer-tagline">
                  {site.footer_tagline || store.footer_tagline_fallback}
                </span>
                <span className="site-footer-location">{site.footer_location || store.footer_location}</span>
              </div>
            </Link>
            <div className="site-footer-brand-connect site-footer-brand-connect--desktop">
              <FooterContactLinks site={site} />
              <FooterSocialIconLinks site={site} brandName={brandName} />
            </div>
          </div>

          <div className="site-footer-mobile-band" aria-label="Hours and social">
            {hoursLine ? <p className="site-footer-hours">{hoursLine}</p> : null}
            <FooterSocialIconLinks site={site} brandName={brandName} />
            <FooterContactLinks site={site} />
          </div>

          <nav className="site-footer-nav" aria-label="Footer navigation">
            <div className="site-footer-nav-pair">
              <FooterNavColumn heading={footerCopy.about_heading} className="site-footer-col--about">
                <FooterNavItem to="/" end onClick={onNavLinkClick('/', { end: true })}>
                  {store.footer_nav_home}
                </FooterNavItem>
                <FooterNavItem to="/about" onClick={onNavLinkClick('/about')}>
                  {store.nav_about}
                </FooterNavItem>
                <FooterNavItem to="/contact" onClick={onNavLinkClick('/contact')}>
                  {store.nav_contact}
                </FooterNavItem>
                <FooterNavItem to="/exhibitions" onClick={onNavLinkClick('/exhibitions')}>
                  Exhibitions
                </FooterNavItem>
              </FooterNavColumn>

              <FooterNavColumn
                heading={footerCopy.customer_heading}
                className="site-footer-col--customer"
              >
                <FooterNavItem to="/track-order" onClick={onNavLinkClick('/track-order')}>
                  {store.footer_nav_track_order}
                </FooterNavItem>
                <FooterNavItem to="/wishlist" onClick={onNavLinkClick('/wishlist')}>
                  {store.footer_nav_wishlist}
                </FooterNavItem>
                {authReady && isAuthed ? (
                  <FooterNavItem to="/account" onClick={onNavLinkClick('/account')}>
                    {store.footer_nav_account}
                  </FooterNavItem>
                ) : authReady ? (
                  <li>
                    <Link
                      to={footerLoginTo}
                      className="site-footer-list-link"
                      onClick={onNavLinkClick('/login', { search: footerLoginSearch })}
                    >
                      {store.footer_nav_sign_in}
                    </Link>
                  </li>
                ) : null}
              </FooterNavColumn>
            </div>

            <div className="site-footer-nav-pair">
              <FooterNavColumn heading={footerCopy.shop_heading} className="site-footer-col--shop">
                <FooterNavItem to="/shop" onClick={onNavLinkClick('/shop')}>
                  {store.footer_nav_shop}
                </FooterNavItem>
                <FooterNavItem to="/search" onClick={onNavLinkClick('/search')}>
                  {store.footer_nav_search}
                </FooterNavItem>
                {isGiftFinderLinkVisible(site.store_copy, 'footer') ? (
                  <FooterNavItem to="/gift-finder" onClick={onNavLinkClick('/gift-finder')}>
                    {site.store_copy?.gift_finder?.nav_label || 'Gift finder'}
                  </FooterNavItem>
                ) : null}
                {isCustomJewelryLinkVisible(site.store_copy, 'footer') ? (
                  <FooterNavItem to="/custom-jewelry" onClick={onNavLinkClick('/custom-jewelry')}>
                    {site.store_copy?.custom_jewelry?.nav_label || 'Custom jewelry design'}
                  </FooterNavItem>
                ) : null}
                <FooterNavItem to="/blog" onClick={onNavLinkClick('/blog')}>
                  {store.footer_nav_blog}
                </FooterNavItem>
              </FooterNavColumn>

              <FooterNavColumn
                heading={footerCopy.policies_heading}
                className="site-footer-col--policies"
              >
                <FooterNavItem to="/policies/shipping" onClick={onNavLinkClick('/policies/shipping')}>
                  {store.footer_nav_shipping}
                </FooterNavItem>
                <FooterNavItem to="/policies/returns" onClick={onNavLinkClick('/policies/returns')}>
                  {store.footer_nav_returns}
                </FooterNavItem>
                <FooterNavItem to="/policies/privacy" onClick={onNavLinkClick('/policies/privacy')}>
                  {store.footer_nav_privacy}
                </FooterNavItem>
              </FooterNavColumn>
            </div>
          </nav>
        </div>

        <FooterPopularSearches storeCopy={site.store_copy} />

        <div className="site-footer-trust">
          <span>{store.footer_made_in}</span>
          <span aria-hidden> · </span>
          <span>{store.trust_badge_2}</span>
          <span aria-hidden> · </span>
          <Link to="/policies/shipping" onClick={onNavLinkClick('/policies/shipping')}>
            {footerCopy.shipping_link_label}
          </Link>
        </div>

        <div className="site-footer-meta">
          <span className="site-footer-copy">
            &copy; {new Date().getFullYear()} {brandName}. {footerCopy.copyright_suffix}
          </span>
          {!authReady ? null : (
            <div className="site-footer-meta-links">
              {isAuthed ? (
                <>
                  {isAdmin ? (
                    <Link
                      to="/admin"
                      className="site-footer-admin"
                      onClick={onNavLinkClick('/admin')}
                    >
                      Store catalog
                    </Link>
                  ) : null}
                </>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}
